import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { Box, Link, Tooltip, Typography, useTheme } from '@mui/material';
import {
  DataGridPro,
  gridClasses,
  gridExpandedSortedRowIdsSelector,
  useGridApiRef,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowClassNameParams,
  type GridRowHeightParams,
  type GridRowHeightReturnValue,
  type GridRowId,
  type GridSlots,
  type PropsFromSlot,
} from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license';
import type { MouseEvent, ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// The grid sources a derived in-memory `ProjectArtifact[]`, so it renders
// `DataGridPro` directly rather than the shared RTK-Query-shaped `DataGrid`
// wrapper. It reuses the shared, data-source-agnostic `DataGridPagination`
// slot for the F9 footer (rows-per-page + "1–25 of N" + page N of M +
// first/last), matching every other grid in the app.
import {
  ArtifactStatusChip,
  DataGridPagination,
  StatusDisplay,
} from '@verantos-dev/shared-components';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { createArtifactSelectionColumn } from './artifact-selection/ArtifactSelectionColumn';
import {
  getProjectArtifactRowId,
  type ProjectArtifactsGridSelection,
} from './artifact-selection/selection';
import {
  LEADING_SLOT_SIZE,
  nameLeadingSlotSx,
  SELECT_CONTROL_CLASS,
  SELECTION_MODE_ATTR,
  selectionColumnSx,
} from './artifact-selection/selectionStyles';
import { ARTIFACT_KIND_ICON } from './artifactKindIcons';
import { ProjectArtifactRowActions } from './ProjectArtifactRowActions';
import { isOrganizationScope } from '../emitters/artifactScope';
import { resolveArtifactStatusChip } from '../emitters/artifactStatusChip';
import type { ProjectArtifact } from '../types';

// Setting the license is idempotent and inexpensive; we duplicate the call
// here so this file's DataGridPro renders licensed regardless of which other
// shared-components surfaces happen to load first.
LicenseInfo.setLicenseKey(
  '7ab0abc518f1ec6ce43e50d7d9280a94Tz0xMjA1NTgsRT0xNzkyMjgxNTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=' // pragma: allowlist secret
);

// Absolute "Last modified" formatting matching the F9 design:
// `May 26, 2026 | 2:47:37 PM`. A fixed date + time keeps the column
// unambiguous (artifacts are long-lived, so relative times like "3 months
// ago" read poorly here). The locale-aware parts use the runtime locale;
// the `|` separator matches the mockup.
const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};
const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

// Only cohorts and analytical datasets are built against a dataset, so only
// their rows carry the second line under the name.
const getDatasetLine = (artifact: ProjectArtifact): string | null =>
  artifact.kind === 'cohort' || artifact.kind === 'analytical_dataset'
    ? artifact.datasetReleaseName ?? null
    : null;

// The kind glyph's box and the name line share one height, so the glyph sits on
// the name rather than centered over a two-line stack.
const NAME_LINE_HEIGHT = LEADING_SLOT_SIZE;

/** Marks the rows that carry a dataset under the name, so only those tighten. */
const STACKED_ROW_CLASS = 'project-artifact-row--stacked';

const stackedRowClassName = ({ row }: GridRowClassNameParams<ProjectArtifact>): string =>
  getDatasetLine(row) ? STACKED_ROW_CLASS : '';

// Only a row with a dataset is measured; the rest keep the grid's default
// single-line height.
const stackedRowHeight = ({ model }: GridRowHeightParams): GridRowHeightReturnValue =>
  getDatasetLine(model as ProjectArtifact) ? 'auto' : null;

const formatLastModified = (iso: string): string => {
  if (!iso) return '—';
  // Backend emits naive UTC timestamps without a timezone suffix; append 'Z'
  // so Date.parse treats the value as UTC rather than local time.
  const normalized = /Z$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const ts = Date.parse(normalized);
  if (Number.isNaN(ts)) return '—';
  const date = new Date(ts);
  return `${date.toLocaleDateString(undefined, DATE_FORMAT)} | ${date.toLocaleTimeString(
    undefined,
    TIME_FORMAT
  )}`;
};

export interface ProjectArtifactsDataGridProps {
  artifacts: ProjectArtifact[];
  isLoading: boolean;
  isError: boolean;
  /** Owning project — threaded to row actions so a variable rename can
   * optimistically patch this list's cache slot (OR-6090). */
  projectId: string;
  /** Route a given artifact points at — used as the Name link's `href` so
   *  Cmd/Ctrl/middle-click open the resource in a new tab natively. */
  getArtifactHref: (artifact: ProjectArtifact) => string;
  /** In-app navigation for a plain (unmodified) click on the Name link. */
  onActivate: (artifact: ProjectArtifact) => void;
  /** Toolbar rendered inside the grid's bordered card, above the column
   *  headers (OR-6123 F9 — the filter/search controls live in the datagrid
   *  header, not a separate page header). */
  header?: ReactNode;
  /** Opt-in row selection. Omitted (the default) leaves a read-only grid; passed
   *  with ``active: false`` it keeps the model controlled but refuses picking. */
  selection?: ProjectArtifactsGridSelection;
}

/**
 * List-view (datagrid) rendering of project artifacts. Columns match the
 * agent-first mockup: name + kind icon, type, status, owner, modified,
 * in-use, and a kebab actions cell. The Name cell is the only clickable
 * navigation target (a real anchor `Link`, matching the cohorts list) — the
 * row itself is not clickable, so the kebab and empty cells don't navigate.
 * A plain click defers to the page-level `onActivate` handler (where the
 * cohort/AV cache priming lives); modified clicks fall through to the
 * anchor `href` so the browser opens the resource in a new tab.
 *
 * Sort/filter/pagination run client-side against the already-filtered
 * `artifacts` array — the page-level kind/search filters remain the
 * source of truth.
 */
export const ProjectArtifactsDataGrid = ({
  artifacts,
  isLoading,
  isError,
  projectId,
  getArtifactHref,
  onActivate,
  header,
  selection,
}: ProjectArtifactsDataGridProps) => {
  const theme = useTheme();

  const isSelectable = Boolean(selection?.active);

  // The first pick holds every row's checkbox open, refused ones included. An
  // attribute so it is one CSS rule, not an inline opacity in every cell.
  const selectionModeAttr = selection && selection.selectedRowIds.size > 0 ? 'on' : 'off';

  // OR-6785: `selection` is rebuilt on every artifact-list poll regardless of
  // whether a user is exporting concept groups, and keying the `columns` memo
  // below off that object made the grid rebuild its full column set on every
  // poll tick, resetting any column width the user had just dragged. Read by
  // the selection column's cells at render time (see
  // `createArtifactSelectionColumn`) instead of a captured closure, so
  // `columns` can key off `selection?.active` rather than the `selection`
  // object itself.
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  const apiRef = useGridApiRef();
  // Sorted and filtered as rendered, across pages — not the `artifacts` order,
  // which is the page's filter output and never re-sorted. Empty before the grid
  // mounts, which select-all reads as "no order to honour".
  const getDisplayedRowIds = useCallback(
    (): readonly GridRowId[] =>
      apiRef.current?.state ? gridExpandedSortedRowIdsSelector(apiRef) : [],
    [apiRef]
  );

  const columns = useMemo<GridColDef<ProjectArtifact>[]>(
    () => [
      // Leads the grid so its control lands in the Name column's gutter.
      ...(selection?.active
        ? [
            createArtifactSelectionColumn(
              // Non-null: this branch only runs while `selection?.active` is
              // true, so `selection` (and therefore the ref) is defined for
              // as long as this column exists.
              () => selectionRef.current as ProjectArtifactsGridSelection,
              getDisplayedRowIds
            ),
          ]
        : []),
      {
        field: 'name',
        headerName: 'Name',
        // Fixed default (not flex) so it renders at this width out of the
        // box; `minWidth` is untouched so a user can still drag it narrower.
        width: 354,
        minWidth: 220,
        // Sort/filter on the visible label (AVs show ``displayName``); the grid
        // orders by the field value, not the rendered node.
        valueGetter: (_: unknown, row: ProjectArtifact) => row.displayName ?? row.name,
        renderCell: (params: GridRenderCellParams<ProjectArtifact, string>) => {
          const Icon = ARTIFACT_KIND_ICON[params.row.kind];
          const href = getArtifactHref(params.row);
          const datasetLine = getDatasetLine(params.row);
          const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
            // Let the browser open a new tab/window for modified clicks
            // (the anchor `href` carries the route). A plain click routes
            // in-app via the page handler (which primes caches first).
            if (event.ctrlKey || event.metaKey || event.shiftKey) return;
            event.preventDefault();
            onActivate(params.row);
          };
          return (
            <Box
              data-testid={`project-artifact-name-cell-${params.row.id}`}
              sx={{
                display: 'flex',
                // A stacked row hangs its content from the name line so the
                // kind glyph tracks the name, not the middle of both lines.
                alignItems: datasetLine ? 'flex-start' : 'center',
                gap: 1.5,
                minWidth: 0,
                width: '100%',
              }}
            >
              {/* The kind glyph. The checkbox appears in the gutter to its left. */}
              <Box
                data-testid={`project-artifact-kind-icon-${params.row.kind}-${params.row.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: LEADING_SLOT_SIZE,
                  height: LEADING_SLOT_SIZE,
                  color: 'text.primary',
                  flexShrink: 0,
                  '& .MuiSvgIcon-root': { fontSize: 16 },
                }}
              >
                <Icon fontSize="small" />
              </Box>
              {/* Name (a real anchor — the sole row navigation target) plus,
                  for organization-scoped rows, a trailing CorporateFare marker,
                  over the dataset the row was built on. The name ellipsizes
                  within the group; the marker hugs the text and never shrinks. */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  // spacing-1 (4px) — the dataset reads as the name's caption,
                  // not as a second stacked field.
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    // Matches the kind glyph's box, which is hung from the same
                    // top edge — that is what centers the glyph on the name.
                    minHeight: `${NAME_LINE_HEIGHT}px`,
                    // spacing-2 (8px) between the name and the trailing
                    // organization-scope marker.
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <Link
                    component={RouterLink}
                    to={href}
                    onClick={handleClick}
                    data-testid={`project-artifact-name-${params.row.kind}-${params.row.id}`}
                    sx={{
                      minWidth: 0,
                      display: 'block',
                      fontSize: theme.typography.body2.fontSize,
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {params.row.displayName ?? params.row.name}
                  </Link>
                  {isOrganizationScope(params.row.scope) && (
                    <Tooltip title="Organization-scoped">
                      <CorporateFareIcon
                        data-testid={`project-artifact-org-icon-${params.row.id}`}
                        aria-label="Organization-scoped"
                        sx={{ flexShrink: 0, fontSize: 16, color: theme.palette.text.secondary }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {datasetLine ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    data-testid={`project-artifact-row-dataset-${params.row.id}`}
                    sx={{
                      minWidth: 0,
                      // body2's leading at caption's size: caption's own 266%
                      // would open a gap wider than the line it sits under.
                      lineHeight: theme.typography.body2.lineHeight,
                    }}
                  >
                    {datasetLine}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        // Fixed, not flex — just enough room for the widest status label
        // ("Processing"); no reason to let it grow on wider viewports.
        width: 130,
        minWidth: 130,
        renderCell: (params: GridRenderCellParams<ProjectArtifact, string>) => {
          const { row } = params;
          const content = (() => {
            // Shared ArtifactStatusChip pill so the list column matches the
            // cohort / AD detail headers.
            if (row.assetStatus) {
              return (
                <ArtifactStatusChip
                  status={row.assetStatus}
                  data-testid={`project-artifact-row-status-${row.id}`}
                />
              );
            }
            // Cohort dot+label visual is the shared, already-DRY
            // ``StatusDisplay`` — keep using it so the cohort row visual
            // matches the rest of the app. Analytical datasets share the same
            // lifecycle vocabulary (Draft / Processing / Processed / Error), so
            // they render with the same status icons. Other kinds route through
            // the central chip resolver so the lifecycle vocabulary is identical
            // wherever the AV / future kinds are rendered.
            if ((row.kind === 'cohort' || row.kind === 'analytical_dataset') && row.status) {
              return (
                <StatusDisplay
                  id={`project-artifact-row-status-${row.id}`}
                  status={`cohortstatus${row.status.toLowerCase()}`}
                />
              );
            }
            const resolved = resolveArtifactStatusChip(row);
            if (!resolved) {
              return (
                <Typography variant="body2" color="text.disabled">
                  —
                </Typography>
              );
            }
            return (
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid={`project-artifact-row-status-${row.id}`}
                data-status-chip={resolved.chip}
              >
                {resolved.label}
              </Typography>
            );
          })();
          return (
            <Box
              data-testid={`project-artifact-row-status-cell-${row.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                // ArtifactStatusChip is shared (used outside this grid too) and
                // has no size-override prop; shrink its label locally so it
                // reads at the same size as the other columns' body2 text,
                // rather than touching the shared component.
                '& .MuiChip-label': {
                  fontSize: theme.typography.body2.fontSize,
                },
              }}
            >
              {content}
            </Box>
          );
        },
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1,
        minWidth: 130,
        valueGetter: (_value, row) => row.owner.name,
      },
      {
        field: 'updatedAt',
        headerName: 'Last modified',
        flex: 1,
        // Wide enough for the full absolute timestamp incl. the AM/PM suffix
        // (e.g. "Jun 9, 2026 | 11:40:01 AM") without clipping at the pinned
        // action column.
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<ProjectArtifact, string>) => (
          <Typography variant="body2" color="text.secondary">
            {formatLastModified(params.row.updatedAt)}
          </Typography>
        ),
      },
      {
        field: 'inUse',
        headerName: 'In use',
        sortable: false,
        filterable: false,
        width: 72,
        // Available-but-not-default column (F9): hidden via the visibility
        // model below, re-showable from the column menu. The platform doesn't
        // expose an "is referenced" flag for any artifact kind yet, so the
        // cell is a placeholder until the manifest endpoint exposes it.
        renderCell: () => (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        // Pinned to the right and excluded from the column-visibility menu so
        // the kebab is always reachable (F9: action column is non-removable).
        hideable: false,
        disableReorder: true,
        align: 'right',
        headerAlign: 'right',
        width: 52,
        renderCell: (params: GridRenderCellParams<ProjectArtifact>) => (
          <ProjectArtifactRowActions artifact={params.row} projectId={projectId} />
        ),
      },
    ],
    [getArtifactHref, onActivate, theme, selection?.active, projectId, getDisplayedRowIds]
  );

  return (
    <Box
      data-testid="project-artifacts-datagrid-layout"
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 400,
        // Bordered, rounded card matching the F9 design (the shared
        // `DataGridLayout` uses the same border/radius intent). The toolbar
        // `header` sits inside this border as the card's top row, above the
        // grid's column headers; `overflow: hidden` keeps both flush to the
        // rounded corners.
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${borderRadius.md}px`,
        overflow: 'hidden',
      }}
    >
      {header}
      {isError ? (
        <Box
          data-testid="project-artifacts-datagrid-error"
          sx={{ padding: 3, color: theme.palette.error.main }}
        >
          <Typography>Could not load project artifacts. Please retry.</Typography>
        </Box>
      ) : (
        <DataGridPro
          data-testid="project-artifacts-datagrid"
          {...{ [SELECTION_MODE_ATTR]: selectionModeAttr }}
          // Read by select-all for the on-screen row order.
          apiRef={apiRef}
          rows={artifacts}
          columns={columns}
          getRowId={getProjectArtifactRowId}
          getRowHeight={stackedRowHeight}
          getRowClassName={stackedRowClassName}
          loading={isLoading}
          // Rows are not clickable — the Name cell is a real anchor `Link`
          // (see the `name` column), so navigation + new-tab behavior lives
          // there, not on the row.
          disableRowSelectionOnClick
          // Controlled only for MUI's `Mui-selected` styling. Picking runs through
          // the collapsed column, hence no `checkboxSelection` (a second column).
          rowSelectionModel={selection?.model}
          // Would sit a few inches from the pill saying the same thing.
          hideFooterSelectedRowCount
          // Client-side pagination footer (rows-per-page + "1–25 of N" + page N
          // of M + first/last) via the shared `DataGridPagination` slot. Default
          // page size 25 matches the F9 design.
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            sorting: { sortModel: [{ field: 'updatedAt', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            // Action kebab pinned right + non-removable (see column def).
            pinnedColumns: { right: ['actions'] },
            // `inUse` is available but off by default.
            columns: { columnVisibilityModel: { inUse: false } },
          }}
          slots={{
            pagination: DataGridPagination as PropsFromSlot<GridSlots['pagination']>,
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            border: 'none',
            // Override the shared MuiDataGrid theme's horizontal cell/header
            // padding (spacing[6] = 24px) down to 16px, for this grid only —
            // not the shared theme layer (muiDataGridThemeConfig.ts). Grouped
            // like MUI's own base rule so headers stay aligned with the cells
            // beneath them.
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
              paddingLeft: theme.spacing(4),
              paddingRight: theme.spacing(4),
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            // A two-line row trades the shared theme's 16px vertical cell
            // padding for 12px, and pins the checkbox to the name line.
            [`& .${STACKED_ROW_CLASS} .${gridClasses.cell}`]: {
              paddingTop: theme.spacing(3),
              paddingBottom: theme.spacing(3),
            },
            [`& .${STACKED_ROW_CLASS} .${SELECT_CONTROL_CLASS}`]: {
              top: `calc(${theme.spacing(3)} + ${NAME_LINE_HEIGHT / 2}px)`,
            },
            // Only once the caller offers selection: with no `selection` prop the
            // grid lays out exactly as it did before the feature existed.
            ...(selection && nameLeadingSlotSx),
            ...(isSelectable && selectionColumnSx(theme)),
          }}
        />
      )}
    </Box>
  );
};
